import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Copy, Factory } from 'lucide-react';
import { productsApi } from '@/api/productsApi';
import { suppliesApi } from '@/api/suppliesApi';
import { salesApi } from '@/api/salesApi';
import { transfersApi } from '@/api/transfersApi';
import { auditApi } from '@/api/auditApi';
import { variantSuppliesApi } from '@/api/variantSuppliesApi';
import { itemTypesApi } from '@/api/itemTypesApi';
import { collectionsApi } from '@/api/collectionsApi';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import { categoriesApi } from '@/api/categoriesApi';
import { DetailPageLayout } from '@/components/detail/DetailPageLayout';
import { EntityLink } from '@/components/detail/EntityLink';
import { StockTable } from '@/components/detail/StockTable';
import { HistoryTable } from '@/components/detail/HistoryTable';
import { Button } from '@/components/common/Button';
import { ProductFormShell } from '@/components/products/ProductForm/ProductFormShell';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { PRODUCT_TYPE_META } from '@/types/api';
import type { Product, ProductVariant, ShelfInventory } from '@/types/api';
import { useAuthStore } from '@/store/authStore';
import { Link } from 'react-router-dom';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'variants', label: 'Variants' },
  { id: 'materials', label: 'Materials' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'sales', label: 'Sales' },
  { id: 'transfers', label: 'Transfers' },
  { id: 'activity', label: 'Activity' },
];

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canViewAudit = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const [showEdit, setShowEdit] = useState(false);
  const [duplicateProduct, setDuplicateProduct] = useState<Product | null>(null);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productsApi.getById(id!),
    enabled: !!id,
  });

  const { data: productSupplies } = useQuery({
    queryKey: ['product-supplies', id],
    queryFn: () => suppliesApi.getProductSupplies(id!),
    enabled: !!id && (activeTab === 'materials' || activeTab === 'overview'),
  });

  const { data: sales } = useQuery({
    queryKey: ['sales-product', id],
    queryFn: () => salesApi.getByProduct(id!),
    enabled: !!id && activeTab === 'sales',
  });

  const { data: transfers } = useQuery({
    queryKey: ['transfers-product', id],
    queryFn: () => transfersApi.getByProduct(id!),
    enabled: !!id && activeTab === 'transfers',
  });

  const { data: auditLogs } = useQuery({
    queryKey: ['audit-product', id],
    queryFn: () => auditApi.getByEntity('PRODUCT', id!),
    enabled: !!id && activeTab === 'activity' && canViewAudit,
  });

  const { data: itemTypes } = useQuery({
    queryKey: ['item-types'],
    queryFn: () => itemTypesApi.getAll(false),
    enabled: activeTab === 'overview',
  });

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => collectionsApi.getAll(false),
    enabled: activeTab === 'overview',
  });

  const { data: productionMethods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
    enabled: activeTab === 'overview',
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll(false),
    enabled: activeTab === 'overview',
  });

  const setTab = (tabId: string) => setSearchParams({ tab: tabId });

  const shelfRows: ShelfInventory[] =
    product?.variants?.flatMap((v) =>
      (v.shelfInventories ?? []).map((si) => ({
        ...si,
        productId: si.productId ?? product.id,
        productName: si.productName ?? product.name,
      }))
    ) ?? [];

  const tabs = canViewAudit ? TABS : TABS.filter((t) => t.id !== 'activity');

  const handleEditSuccess = () => {
    setShowEdit(false);
    queryClient.invalidateQueries({ queryKey: ['product', id] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const itemTypeName = itemTypes?.find((t) => t.id === product?.itemTypeId)?.name;
  const collectionName = collections?.find((c) => c.id === product?.collectionId)?.name;
  const methodNames = productionMethods
    ?.filter((m) => product?.productionMethodIds?.includes(m.id))
    .map((m) => m.name)
    .join(', ');
  const categoryNames = categories
    ?.filter((c) => product?.categories?.some((pc) => pc.id === c.id))
    .map((c) => c.name)
    .join(', ');

  return (
    <>
      <DetailPageLayout
        backTo="/products"
        backLabel="Products"
        title={product?.name ?? 'Product'}
        subtitle={product ? `${product.sku} · ${PRODUCT_TYPE_META[product.productType]?.label ?? product.productType}` : undefined}
        isLoading={isLoading}
        notFound={isError || (!isLoading && !product)}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setTab}
        actions={
          product && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setShowEdit(true)}>
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDuplicateProduct(product)}>
                <Copy className="w-4 h-4 mr-1" /> Duplicate
              </Button>
              <Link to="/production">
                <Button variant="ghost" size="sm">
                  <Factory className="w-4 h-4 mr-1" /> Production
                </Button>
              </Link>
            </>
          )
        }
      >
        {product && activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-warm-cream">Details</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-gray-500">SKU</dt><dd className="mono">{product.sku}</dd></div>
                <div><dt className="text-gray-500">Price</dt><dd>{formatCurrency(product.basePrice)}</dd></div>
                <div><dt className="text-gray-500">Cost</dt><dd>{formatCurrency(product.cost)}</dd></div>
                <div><dt className="text-gray-500">Item Type</dt><dd>{itemTypeName ?? '—'}</dd></div>
                <div><dt className="text-gray-500">Collection</dt><dd>{collectionName ?? '—'}</dd></div>
                <div><dt className="text-gray-500">Design Families</dt><dd>{categoryNames || product.category || '—'}</dd></div>
                <div className="col-span-2"><dt className="text-gray-500">Production Methods</dt><dd>{methodNames || '—'}</dd></div>
                {product.material && (
                  <div className="col-span-2"><dt className="text-gray-500">Material</dt><dd>{product.material}</dd></div>
                )}
                {(product.dimensionHeight || product.dimensionWidth || product.dimensionDepth) && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Dimensions (H×W×D)</dt>
                    <dd>{[product.dimensionHeight, product.dimensionWidth, product.dimensionDepth].filter(Boolean).join(' × ')}</dd>
                  </div>
                )}
              </dl>
              {product.description && (
                <p className="text-sm text-gray-600 dark:text-warm-sand pt-2 border-t border-gray-200 dark:border-charcoal-700">{product.description}</p>
              )}
            </div>
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-warm-cream">Summary</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-gray-500">Variants</dt><dd>{product.variants?.length ?? 0}</dd></div>
                <div><dt className="text-gray-500">Units on shelf</dt><dd>{shelfRows.reduce((s, r) => s + r.quantityOnShelf, 0)}</dd></div>
                <div><dt className="text-gray-500">BOM items</dt><dd>{productSupplies?.length ?? 0}</dd></div>
                <div><dt className="text-gray-500">3D Printed</dt><dd>{product.is3dPrinted ? 'Yes' : 'No'}</dd></div>
              </dl>
              {(product.designFilePath || product.modelFilePath) && (
                <div className="pt-2 border-t border-gray-200 dark:border-charcoal-700 text-sm space-y-1">
                  {product.designFilePath && <p>Design: {product.designFileName ?? product.designFilePath}</p>}
                  {product.modelFilePath && <p>Model: {product.modelFilePath}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {product && activeTab === 'variants' && (
          <VariantsTab variants={product.variants ?? []} />
        )}

        {product && activeTab === 'materials' && (
          <MaterialsTab variants={product.variants ?? []} productSupplies={productSupplies ?? []} />
        )}

        {product && activeTab === 'inventory' && (
          <StockTable items={shelfRows} showProduct={false} emptyMessage="No shelf inventory for this product." />
        )}

        {product && activeTab === 'sales' && (
          <HistoryTable
            rows={sales ?? []}
            rowKey={(s) => s.id}
            emptyMessage="No sales recorded for this product."
            columns={[
              { key: 'date', header: 'Date', render: (s) => formatDate(s.saleDate) },
              { key: 'location', header: 'Location', render: (s) => <EntityLink type="location" id={s.locationId} label={s.locationName} /> },
              { key: 'sku', header: 'Variant', render: (s) => <span className="mono text-sm">{s.productVariantSku}</span> },
              { key: 'qty', header: 'Qty', align: 'right', render: (s) => s.quantity },
              { key: 'price', header: 'Total', align: 'right', render: (s) => formatCurrency(s.totalSaleAmount) },
            ]}
          />
        )}

        {product && activeTab === 'transfers' && (
          <HistoryTable
            rows={transfers ?? []}
            rowKey={(t) => t.id}
            emptyMessage="No transfers recorded for this product."
            columns={[
              { key: 'date', header: 'Date', render: (t) => formatDate(t.transferDate) },
              { key: 'from', header: 'From', render: (t) => <EntityLink type="location" id={t.fromLocationId} label={t.fromLocationName} /> },
              { key: 'to', header: 'To', render: (t) => <EntityLink type="location" id={t.toLocationId} label={t.toLocationName} /> },
              { key: 'sku', header: 'Variant', render: (t) => <span className="mono text-sm">{t.productVariantSku}</span> },
              { key: 'qty', header: 'Qty', align: 'right', render: (t) => t.quantity },
            ]}
          />
        )}

        {product && activeTab === 'activity' && canViewAudit && (
          <HistoryTable
            rows={auditLogs ?? []}
            rowKey={(e) => e.id}
            emptyMessage="No activity logged for this product."
            columns={[
              { key: 'date', header: 'When', render: (e) => formatDate(e.createdAt) },
              { key: 'action', header: 'Action', render: (e) => e.actionType },
              { key: 'desc', header: 'Description', render: (e) => e.description },
              { key: 'user', header: 'User', render: (e) => e.userName ?? '—' },
            ]}
          />
        )}
      </DetailPageLayout>

      {showEdit && product && (
        <ProductFormShell
          isOpen
          mode="edit"
          product={product}
          onClose={() => setShowEdit(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {duplicateProduct && (
        <ProductFormShell
          isOpen
          mode="duplicate"
          product={duplicateProduct}
          onClose={() => setDuplicateProduct(null)}
          onSuccess={() => {
            setDuplicateProduct(null);
            queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}
    </>
  );
}

function VariantsTab({ variants }: { variants: ProductVariant[] }) {
  if (!variants.length) {
    return <p className="text-gray-600 dark:text-warm-sand py-8 text-center">No variants configured.</p>;
  }

  return (
    <div className="space-y-4">
      {variants.map((v) => (
        <div key={v.id} className="card p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium text-gray-900 dark:text-warm-cream">{v.displayName}</p>
              <p className="text-sm mono text-gray-600 dark:text-warm-sand">{v.fullSku || v.variantSku}</p>
            </div>
            <div className="text-sm text-right">
              <p className="text-green-600 dark:text-green-400 font-medium">{v.unitsMakeable} makeable</p>
              <p className="text-gray-500">{v.totalGrams?.toFixed?.(1) ?? v.totalGrams}g filament</p>
            </div>
          </div>
          {v.variantFilaments?.length > 0 && (
            <ul className="mt-3 text-sm space-y-1">
              {v.variantFilaments.map((vf) => (
                <li key={vf.id} className="flex justify-between text-gray-600 dark:text-warm-sand">
                  <EntityLink type="supply" id={vf.filament?.id} label={vf.filament?.name ?? 'Filament'} />
                  <span>{vf.quantityGrams}g</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function MaterialsTab({
  variants,
  productSupplies,
}: {
  variants: ProductVariant[];
  productSupplies: Awaited<ReturnType<typeof suppliesApi.getProductSupplies>>;
}) {
  const variantIds = variants.map((v) => v.id);

  const { data: variantSuppliesMap } = useQuery({
    queryKey: ['variant-supplies-batch', variantIds],
    queryFn: async () => {
      const entries = await Promise.all(
        variantIds.map(async (vid) => [vid, await variantSuppliesApi.getForVariant(vid)] as const)
      );
      return Object.fromEntries(entries);
    },
    enabled: variantIds.length > 0,
  });

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-warm-cream mb-3">Product-level BOM</h3>
        {productSupplies.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-warm-sand">No product-level supplies.</p>
        ) : (
          <ul className="card divide-y divide-gray-200 dark:divide-charcoal-700">
            {productSupplies.map((ps) => (
              <li key={ps.id} className="px-4 py-3 flex justify-between text-sm">
                <EntityLink type="supply" id={ps.supplyId} label={ps.supplyName} />
                <span>{ps.quantityRequired} {ps.unitOfMeasure}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {variants.map((v) => {
        const vSupplies = variantSuppliesMap?.[v.id] ?? [];
        if (!vSupplies.length) return null;
        return (
          <section key={v.id}>
            <h3 className="font-semibold text-gray-900 dark:text-warm-cream mb-3">{v.displayName} supplies</h3>
            <ul className="card divide-y divide-gray-200 dark:divide-charcoal-700">
              {vSupplies.map((vs) => (
                <li key={vs.id} className="px-4 py-3 flex justify-between text-sm">
                  <EntityLink type="supply" id={vs.supplyId} label={vs.supplyName} />
                  <span>{vs.quantityRequired} {vs.unitOfMeasure}</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
